import { useEffect, useRef, useState, useMemo } from 'react'
import { SwatchPicker, OptionGroup, SizeSlider, pickByFamily } from './SwatchPicker'
import {
  OVERLAY_FONTS,
  GRADIENT_PRESETS,
  OUTLINE_COLORS,
  ensureFontLoaded,
  renderTextMask,
  resolveGradientPreset,
  resolveOutlineColor,
  makeSilhouetteOutline,
  buildLegend,
} from '../utils/textRender'

function drawPattern(canvas, pattern, brand) {
  if (!pattern || !canvas) return
  const { grid, width, height } = pattern
  const cell = Math.max(4, Math.floor(canvas.width / width))
  canvas.width = width * cell
  canvas.height = height * cell
  const ctx = canvas.getContext('2d')
  const colorMap = new Map(brand.colors.map((c) => [c.id, c.hex]))

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      ctx.fillStyle = colorMap.get(grid[y][x]) || '#fff'
      ctx.fillRect(x * cell, y * cell, cell, cell)
    }
  }
}

export default function TextBeadModal({ open, brand, onClose, onConfirm, initial }) {
  const [text, setText] = useState('豆田')
  const [fontKey, setFontKey] = useState('pixel')
  const [charHeight, setCharHeight] = useState(12)
  const [textColorId, setTextColorId] = useState(brand.colors[0]?.id)
  const [borderColorId, setBorderColorId] = useState(brand.colors[1]?.id || brand.colors[0]?.id)
  const [bgColorId, setBgColorId] = useState(brand.colors.find((c) => c.hex.toLowerCase() === '#ffffff')?.id || brand.colors[0]?.id)
  const [borderThickness, setBorderThickness] = useState(1)
  // 渐变色（预设模板，不可自定义编辑）
  const [colorMode, setColorMode] = useState('solid') // 'solid' | 渐变预设 key
  // 整体描边
  const [outlineEnabled, setOutlineEnabled] = useState(false)
  const [outlineColorKey, setOutlineColorKey] = useState('black')
  const [outlineThickness, setOutlineThickness] = useState(1)
  const [outlineGap, setOutlineGap] = useState(1)
  const [previewPattern, setPreviewPattern] = useState(null)
  const [fontTick, setFontTick] = useState(0)
  const previewCanvasRef = useRef(null)

  const font = OVERLAY_FONTS.find((f) => f.key === fontKey) || OVERLAY_FONTS[0]
  const activePreset = GRADIENT_PRESETS.find((p) => p.key === colorMode) || null
  const gradientIds = useMemo(
    () => (activePreset ? resolveGradientPreset(activePreset, brand.colors) : null),
    [activePreset, brand]
  )
  const outlineColorId = useMemo(() => {
    const preset = OUTLINE_COLORS.find((c) => c.key === outlineColorKey) || OUTLINE_COLORS[0]
    return resolveOutlineColor(preset.hex, brand.colors)
  }, [outlineColorKey, brand])

  // 边框/背景：每个色系只保留 1 个代表色（深色边框 / 浅色背景）
  const borderColors = useMemo(() => pickByFamily(brand.colors, 'dark'), [brand])
  const bgColors = useMemo(() => pickByFamily(brand.colors, 'light'), [brand])

  // 打开弹窗/切换字体时按需加载字体（与叠加图纸同一渲染管线），加载完成后重绘
  useEffect(() => {
    if (!open) return
    let cancelled = false
    ensureFontLoaded(font, text || '拼豆').then(() => {
      if (!cancelled) setFontTick((t) => t + 1)
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fontKey])

  // 重新编辑：打开时回填上次生成时的配置
  useEffect(() => {
    if (!open || !initial) return
    setText(initial.text ?? '豆田')
    setFontKey(initial.fontKey ?? 'pixel')
    setCharHeight(initial.charHeight ?? 12)
    setTextColorId(initial.textColorId ?? brand.colors[0]?.id)
    setBorderColorId(initial.borderColorId ?? brand.colors[1]?.id ?? brand.colors[0]?.id)
    setBgColorId(initial.bgColorId ?? brand.colors.find((c) => c.hex.toLowerCase() === '#ffffff')?.id ?? brand.colors[0]?.id)
    setBorderThickness(initial.borderThickness ?? 1)
    setColorMode(initial.colorMode ?? 'solid')
    setOutlineEnabled(Boolean(initial.outlineEnabled))
    setOutlineColorKey(initial.outlineColorKey ?? 'black')
    setOutlineThickness(initial.outlineThickness ?? 1)
    setOutlineGap(initial.outlineGap ?? 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const generatePattern = () => {
    if (!text.trim()) return null

    // 共享文字渲染内核：支持 6 款字体、多行换行（每行居中）、无级字号
    const mask = renderTextMask(text, font, charHeight)
    if (!mask) return null

    // 整体轮廓柔性描边（与叠加图纸同一算法）
    const outline = outlineEnabled && outlineColorId ? makeSilhouetteOutline(mask, outlineThickness, charHeight, outlineGap) : null

    // 网格需容纳描边的外扩范围
    const contentMinX = outline ? outline.offsetX : 0
    const contentMinY = outline ? outline.offsetY : 0
    const contentW = Math.max(mask.w, outline ? outline.offsetX + outline.w : 0) - contentMinX
    const contentH = Math.max(mask.h, outline ? outline.offsetY + outline.h : 0) - contentMinY
    const width = contentW + borderThickness * 2
    const height = contentH + borderThickness * 2

    const grid = []
    const borderColor = borderColorId
    const bgColor = bgColorId

    for (let y = 0; y < height; y++) {
      const row = []
      for (let x = 0; x < width; x++) {
        if (
          x < borderThickness ||
          x >= width - borderThickness ||
          y < borderThickness ||
          y >= height - borderThickness
        ) {
          row.push(borderColor)
          continue
        }
        // 内容区坐标（mask 空间）
        const sx = x - borderThickness + contentMinX
        const sy = y - borderThickness + contentMinY
        const inText = sx >= 0 && sx < mask.w && sy >= 0 && sy < mask.h && mask.mask[sy * mask.w + sx] === 1
        let inOutline = false
        if (outline) {
          const ox = sx - outline.offsetX
          const oy = sy - outline.offsetY
          inOutline = ox >= 0 && ox < outline.w && oy >= 0 && oy < outline.h && outline.mask[oy * outline.w + ox] === 1
        }
        if (inText) {
          // 三色渐变：按当前格在文字高度的相对位置分三段取色
          if (gradientIds) {
            row.push(gradientIds[Math.min(2, Math.floor((sy / mask.h) * 3))])
          } else {
            row.push(textColorId)
          }
        } else if (inOutline) {
          row.push(outlineColorId)
        } else {
          row.push(bgColor)
        }
      }
      grid.push(row)
    }

    const { legend, total } = buildLegend(grid, brand)
    return { grid, legend, width, height, total, brandId: brand.id }
  }

  useEffect(() => {
    if (!open) return
    const pattern = generatePattern()
    setPreviewPattern(pattern)
    drawPattern(previewCanvasRef.current, pattern, brand)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, text, fontKey, charHeight, colorMode, textColorId, borderColorId, bgColorId, borderThickness, outlineEnabled, outlineColorId, outlineThickness, outlineGap, gradientIds, brand, fontTick])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="flex w-full max-w-2xl flex-col gap-4 rounded-3xl bg-white p-5 shadow-card max-h-[95vh] overflow-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-cocoa">文字拼豆</h3>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-cocoa-light transition hover:bg-cream-100 hover:text-cocoa"
          >
            ✕
          </button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-cocoa-light">文字内容（支持换行，每行居中）</label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={24}
                rows={2}
                className="form-input resize-none"
                placeholder="输入文字，回车换行"
              />
            </div>

            <OptionGroup
              label="字体"
              value={fontKey}
              onChange={setFontKey}
              options={OVERLAY_FONTS.map((f) => ({ value: f.key, label: f.label }))}
            />

            <SizeSlider
              label="字号（无级调节）"
              value={charHeight}
              onChange={setCharHeight}
              min={8}
              max={64}
            />

            <OptionGroup
              label="边框厚度"
              value={borderThickness}
              onChange={setBorderThickness}
              options={[0, 1, 2, 3].map((t) => ({ value: t, label: t === 0 ? '无边框' : `${t} 格` }))}
            />
          </div>

          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-cocoa-light">文字配色</label>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => setColorMode('solid')}
                  className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                    colorMode === 'solid'
                      ? 'border-blush bg-blush text-white'
                      : 'border-lavender/40 bg-white text-cocoa hover:border-blush'
                  }`}
                >
                  单色
                </button>
                {GRADIENT_PRESETS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setColorMode(p.key)}
                    className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition ${
                      colorMode === p.key
                        ? 'border-blush bg-blush text-white'
                        : 'border-lavender/40 bg-white text-cocoa hover:border-blush'
                    }`}
                  >
                    <span className="inline-flex w-4 flex-col overflow-hidden rounded-sm border border-black/10">
                      {p.colors.map((hex) => (
                        <span key={hex} className="h-1.5 w-full" style={{ backgroundColor: hex }} />
                      ))}
                    </span>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            {colorMode === 'solid' && (
              <SwatchPicker label="文字颜色" value={textColorId} onChange={setTextColorId} colors={brand.colors} />
            )}
            <div>
              <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-cocoa-light">
                <input
                  type="checkbox"
                  checked={outlineEnabled}
                  onChange={(e) => setOutlineEnabled(e.target.checked)}
                  className="h-4 w-4 accent-blush"
                />
                文字整体描边（柔性圆角，沿文字整体轮廓）
              </label>
              {outlineEnabled && (
                <div className="mt-2 space-y-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-cocoa-light">厚度</span>
                    {[1, 2, 3].map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setOutlineThickness(t)}
                        className={`rounded-lg border px-3 py-1 text-xs font-semibold transition ${
                          outlineThickness === t
                            ? 'border-blush bg-blush text-white'
                            : 'border-lavender/40 bg-white text-cocoa hover:border-blush'
                        }`}
                      >
                        {t} 格
                      </button>
                    ))}
                    <span className="ml-2 text-xs text-cocoa-light">安全距离</span>
                    {[1, 2, 3].map((g) => (
                      <button
                        key={g}
                        type="button"
                        onClick={() => setOutlineGap(g)}
                        className={`rounded-lg border px-3 py-1 text-xs font-semibold transition ${
                          outlineGap === g
                            ? 'border-blush bg-blush text-white'
                            : 'border-lavender/40 bg-white text-cocoa hover:border-blush'
                        }`}
                      >
                        {g} 格
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {OUTLINE_COLORS.map((c) => (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => setOutlineColorKey(c.key)}
                        title={c.label}
                        className={`h-7 w-7 rounded-md border-2 transition ${
                          outlineColorKey === c.key
                            ? 'border-blush scale-110 shadow-sm'
                            : 'border-black/10 hover:scale-105'
                        }`}
                        style={{ backgroundColor: c.hex }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
            <SwatchPicker label="边框颜色" value={borderColorId} onChange={setBorderColorId} colors={borderColors} maxHeight="max-h-20" />
            <SwatchPicker label="背景颜色" value={bgColorId} onChange={setBgColorId} colors={bgColors} maxHeight="max-h-20" />
          </div>
        </div>

        <div>
          <p className="mb-2 text-xs font-medium text-cocoa-light">预览</p>
          <canvas
            ref={previewCanvasRef}
            className="w-full rounded-2xl border border-lavender/30 bg-cream-100"
            style={{ imageRendering: 'pixelated' }}
          />
          <p className="mt-1 text-xs text-cocoa-light">
            {previewPattern?.width || 0}×{previewPattern?.height || 0} · {previewPattern?.legend.length || 0} 种颜色
          </p>
        </div>

        <div className="flex items-end justify-between gap-2">
          <p className="text-[11px] leading-4 text-cocoa-light/80">
            全部字体均遵循 SIL OFL 1.1 开源协议，生成的图纸可免费用于商业用途。
          </p>
          <div className="flex shrink-0 justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-full border border-lavender/60 px-5 py-2 text-sm font-medium text-cocoa transition hover:bg-lavender/30"
          >
            取消
          </button>
          <button
            onClick={() => {
              const pattern = generatePattern()
              if (pattern) {
                onConfirm(pattern, {
                  kind: 'single',
                  config: {
                    text, fontKey, charHeight, textColorId, borderColorId, bgColorId,
                    borderThickness, colorMode, outlineEnabled, outlineColorKey, outlineThickness, outlineGap,
                  },
                })
                onClose()
              }
            }}
            className="rounded-full bg-blush px-5 py-2 text-sm font-semibold text-white shadow-soft transition hover:bg-blush-dark hover:shadow-md"
          >
            确认生成
          </button>
          </div>
        </div>
      </div>
    </div>
  )
}
